import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { UserAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import AlertPopup from '../AlertPopup';
import { MessageCircle, BookOpen, FileText, Save, ChevronLeft, ChevronRight, CreditCard, MessageSquare, LogOut, Menu, Heart, ExternalLink, ChevronDown } from 'lucide-react';
import '../../styles/ProtectedLayout.css';
import logoLight from '../../images/logoMain.png';
import logoDark from '../../images/logoMain-D.png';
import collapseD from '../../images/collapsedD.png';
import collapseL from '../../images/collapsedL.png';

const ProtectedLayout = () => {
  const { logout, fetchNotifications, token, user } = UserAuth();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [localNotifications, setLocalNotifications] = useState([]);
  const [isNotificationPopupVisible, setIsNotificationPopupVisible] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isFeedbackExpanded, setIsFeedbackExpanded] = useState(false);
  const [subscriptionData, setSubscriptionData] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { isDarkMode, themeClass } = useTheme();

  // Detectar cambios de tamaño de pantalla
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Fetch subscription data
  useEffect(() => {
    const fetchSubscriptionData = async () => {
      try {
        if (user && token) {
          const idTokenResult = await user.getIdTokenResult();   
          setSubscriptionData({
            user: {
              email: user.email || 'usuario@gmail.com',
              picture: user.photoURL || null,
              name: user.displayName || 'Usuario'
            },
            plan: idTokenResult.claims.plan || 'free'
          });
        }
      } catch (error) {
        console.error('Error fetching subscription data:', error);
        setSubscriptionData({
          user: {
            email: user.email || 'usuario@gmail.com',
            picture: user.photoURL || null,
            name: user.displayName || 'Usuario'
          },
          plan: 'free'   // fallback consistente
        });
      }
    };
    if (token && user) {
      fetchSubscriptionData();
    }
  }, [token, user]);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
      setIsUserMenuOpen(false);
    } catch (e) {
      alert(e.message);
    }
  };

  useEffect(() => {
    if (location.pathname === '/account' || location.pathname === '/account/') {
      navigate('/account/Temis', { replace: true });
    }
  }, [location.pathname, navigate]);

  useEffect(() => {
    const getNotifications = async () => {
      const fetchedNotifications = await fetchNotifications();
      setLocalNotifications(fetchedNotifications);
    };

    getNotifications();
  }, []);

  const menuItems = [
    { path: '/account/Temis', icon: MessageCircle, label: 'Chat' },
    { path: '/account/manual', icon: BookOpen, label: 'Manual' },
    { path: '/account/politica', icon: FileText, label: 'Política' },
    { path: '/account/guardado', icon: Save, label: 'Guardado' }
  ];

  const isActive = (path) => {
    return location.pathname === path;
  };

  const handleMenuItemClick = (path) => {
    navigate(path);
    setIsMobileSidebarOpen(false);
  };

  const getPlanInitials = (planName) => {
    if (!planName) return 'T';
    return planName.split(' ').map(word => word[0]).join('').toUpperCase().slice(0, 2);
  };

  return (
    <div className="protected-layout">
      {isNotificationPopupVisible && (
        <AlertPopup
          title={localNotifications[0]?.titulo || ''}
          message={localNotifications[0]?.texto || ''}
          onClose={() => setIsNotificationPopupVisible(false)}
        />
      )}

      <button 
        className={`hamburger-menu ${themeClass}`}
        onClick={() => setIsMobileSidebarOpen(!isMobileSidebarOpen)}
      >
        <Menu size={24} />
      </button>

      <div className={`sidebar ${isCollapsed && !isMobile ? 'collapsed' : ''} ${themeClass} ${isMobileSidebarOpen ? 'show' : ''}`}>
        {!isMobile && (
          <button 
            className={`collapse-btn ${themeClass}`}
            onClick={() => setIsCollapsed(!isCollapsed)}
          >
            {isCollapsed ? <ChevronRight size={20} /> : <ChevronLeft size={20} />}
          </button>
        )}

        {isMobile && isMobileSidebarOpen && (
          <button 
            className={`collapse-btn ${themeClass}`}
            onClick={() => setIsMobileSidebarOpen(false)}
          >
            <img 
              src={isDarkMode ? logoDark : logoLight}
              alt="cerrar"
              className="collapsed-icon"
            />
          </button>
        )}

        <div className={`logo-section ${themeClass}`}>
          {isCollapsed && !isMobile ? (
            <img 
              src={isDarkMode ? collapseD : collapseL}
              alt="temis"
              className="collapsed-icon"
            />
          ) : (
            <img 
              src={isDarkMode ? logoDark : logoLight}
              alt="temis"
              className="expanded-logo"
            />
          )}
        </div>

        <nav className={`menu-nav ${themeClass} ${isCollapsed && !isMobile ? 'collapsed-nav' : ''}`}>
          {menuItems.map((item) => {
            const Icon = item.icon;
            return (
              <button
                key={item.path}
                onClick={() => handleMenuItemClick(item.path)}
                className={`menu-log ${isActive(item.path) ? 'active' : ''} ${themeClass}`}
                title={item.label}
              >
                {!isMobile && <Icon size={20} />}
                {(!isCollapsed || isMobile) && <span>{item.label}</span>}
              </button>
            );
          })}
        </nav>

        <div className={`sidebar-footer ${themeClass}`}>
          <button 
            className={`user-info ${themeClass}`}
            onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
          >
            <div className={`user-avatar ${themeClass}`}>
              {subscriptionData?.user.picture ? (
                <img 
                  src={subscriptionData.user.picture} 
                  alt="Profile"
                  className="avatar-image"
                />
              ) : (
                <span>{getPlanInitials(subscriptionData?.plan)}</span>
              )}
            </div>
            {(!isCollapsed || isMobile) && subscriptionData && (
              <div className={`user-details ${themeClass}`}>
                <span className={`user-name ${themeClass}`}>Plan {subscriptionData.plan}</span>
                <span className={`user-email ${themeClass}`}>{subscriptionData.user.email}</span>
              </div>
            )}
          </button>

          {isUserMenuOpen && (
            <>
              <div 
                className="user-menu-overlay"
                onClick={() => setIsUserMenuOpen(false)}
              />
              <div className={`user-menu ${themeClass} ${isCollapsed && !isMobile ? 'collapsed-nav' : ''}`}>
                <div className={`user-menu-header ${themeClass}`}>
                  <div className={`user-avatar-large ${themeClass}`}>
                    {subscriptionData?.user.picture ? (
                      <img 
                        src={subscriptionData.user.picture} 
                        alt="Profile"
                        className="avatar-image"
                      />
                    ) : (
                      <span>{getPlanInitials(subscriptionData?.plan)}</span>
                    )}
                  </div>
                  <div className={`user-menu-info ${themeClass}`}>
                    <h3>Plan {subscriptionData?.plan || 'Plan default'}</h3>
                    <p>{subscriptionData?.user.email || 'usuario@gmail.com'}</p>
                  </div>
                </div>
                <div className={`user-menu-divider ${themeClass}`} />
                <button 
                  className={`user-menu-log ${themeClass}`}
                  onClick={() => {
                    navigate('/account/new');
                    setIsUserMenuOpen(false);
                  }}
                >
                  <CreditCard size={18} />
                  <span>Planes</span>
                </button>
                
                {/* Feedback Expandible */}
                <div className={`feeedback  ${themeClass}`} >
                  <button 
                    className={`user-menu-log ${themeClass}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsFeedbackExpanded(!isFeedbackExpanded);
                    }}
                  >
                    <MessageSquare size={18} />
                    <span>Enviar feedback</span>
                </button>

                  <div style={{ 
                    maxHeight: isFeedbackExpanded ? '200px' : '0',
                    overflow: 'hidden',
                    transition: 'max-height 0.3s ease, padding 0.3s ease',
                    padding: isFeedbackExpanded ? '0 16px 12px 16px' : '0 16px'
                  }}>
                    <div className={`giveusfeedback ${themeClass}`}>
                      <Heart 
                        size={14} 
                        style={{ 
                          verticalAlign: 'middle', 
                          marginRight: '6px',
                          color: 'var(--Red)'
                        }} 
                      />
                      Queremos saber qué documentos y características te gustaría ver en nuestra plataforma.
                    </div>

                    <a 
                      href="https://forms.gle/1ajtPSG784ei4xPw9" 
                      target="_blank" 
                      rel="noreferrer"
                      onClick={(e) => {
                        e.stopPropagation();
                        setIsUserMenuOpen(false);
                      }}
                      className={`givefeedback ${themeClass}`}
                    >
                      Dar Feedback
                      <ExternalLink size={14} />
                    </a>
                  </div>
                </div>

                <button 
                  className={`user-menu-log logout ${themeClass}`}
                  onClick={handleLogout}
                >
                  <LogOut size={18} />
                  <span>Cerrar sesión</span>
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      <div className={`main-contentMenu ${themeClass}`}>
        <Outlet />
      </div>
    </div>
  );
};

export default ProtectedLayout;